import { memo, useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  ItemTree,
  useItemLockState,
  useTreeSelection,
  type SelectMode,
} from "@repo/shared-components";
import { cn } from "@repo/utils";
import { ResizeHandleOnMouseDown } from "~/components/ui/resize-handle-on-mouse-down";
import { Title } from "~/components/ui/title";
import { useResizableWidth } from "~/hooks/use-resizable-width";
import { TaskExplorerToolbar } from "./toolbar/task-explorer-toolbar";
import { useTaskSorting } from "../../hooks/use-task-sorting";
import { useTabWindowsContext } from "../../tab-windows/hooks/use-tab-windows-context";
import {
  SortBy,
  type SortType,
  type TabWindow,
  type ExplorerItem,
  type StatusGroup,
} from "../../task.types";
import { ChangeStatusPanel } from "../panel/change-status/change-status-panel";
import { ListsPanel } from "../panel/list/lists-panel";
import { PANEL_BUTTONS, TaskPanel, type PanelButton } from "../panel/task-panel";

type Props = {
  window: TabWindow<StatusGroup>;
};

const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 384;

export const TaskExplorer = memo<Props>(({ window }: Props) => {
  const { user } = useAuth();
  const currentUserId = user?.profile?.sub ?? null;
  const {
    activePanelWindowId,
    closeWindow,
    updateWindowState,
    changeWindowOrder,
    handleItemSelection,
    togglePanelForWindow,
    updateDetailViewForWindow,
  } = useTabWindowsContext<ExplorerItem>();

  const [activePanelButtons, setActivePanelButtons] = useState<Set<PanelButton>>(new Set());
  const showChangeStatusPanel = activePanelButtons.has(PANEL_BUTTONS.ChangeStatus);
  const showListsPanel = activePanelButtons.has(PANEL_BUTTONS.List);

  const { width, isResizing, onResizeStart } = useResizableWidth({
    min: MIN_WIDTH,
    max: MAX_WIDTH,
    initial: DEFAULT_WIDTH,
  });

  // Default to Ascending direction
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Default to Workflow if sortType is undefined
  const currentSortType = window.state.sortType ?? SortBy.Workflow;

  const { sortedGroups, displayItems } = useTaskSorting(
    window.items as StatusGroup[],
    currentSortType,
    sortDirection
  );

  const isPanelOn = activePanelWindowId === window.id;

  const handleKeyboardNavigate = useCallback(
    (itemId: number, selectedItems: Set<number>) => {
      updateWindowState(window.id, { ...window.state, selectedItems });

      if (selectedItems.size === 0) return;
      updateDetailViewForWindow(window.id, selectedItems, true);
    },
    [window.id, window.state, updateWindowState, updateDetailViewForWindow]
  );

  const {
    leafCount: totalLeafCount,
    disabledIds: disabledItemIds,
    selectableIds: selectableItemIds,
  } = useItemLockState(window.items as ExplorerItem[], currentUserId);

  const handleSelectItem = useCallback(
    (itemId: number, mode: SelectMode) =>
      handleItemSelection(window.id, itemId, mode === "toggle", isPanelOn),
    [handleItemSelection, window.id, isPanelOn]
  );

  const handleSelectSet = useCallback(
    (ids: Set<number>) => handleItemSelection(window.id, ids, false, isPanelOn),
    [handleItemSelection, window.id, isPanelOn]
  );

  const { containerRef, handleItemClick, handleKeyDown } = useTreeSelection({
    enabled: !window.state.isMinimized,
    windowId: window.id,
    selectableIds: selectableItemIds,
    disabledIds: disabledItemIds,
    onSelectItem: handleSelectItem,
    onSelectSet: handleSelectSet,
    onNavigate: handleKeyboardNavigate,
  });

  const handleActivePanelButton = (button: PanelButton) => {
    setActivePanelButtons((prev) => {
      if (prev.has(button)) {
        return new Set();
      }

      return new Set([button]);
    });
  };

  const handleToggleItemPanel = () => {
    togglePanelForWindow(window.id);
  };

  const handleSortChange = useCallback(
    (sortType: SortType | undefined, direction: "asc" | "desc" = "asc") => {
      setSortDirection(direction);
      updateWindowState(window.id, { ...window.state, sortType });
    },
    [window.id, window.state, updateWindowState]
  );

  const handleMinimize = () => {
    updateWindowState(window.id, { ...window.state, isMinimized: !window.state.isMinimized });
  };

  const handlePin = () => {
    updateWindowState(window.id, { ...window.state, isPinned: !window.state.isPinned });
  };

  const handleClose = () => {
    closeWindow(window.id);
  };

  const handleMoveLeft = () => {
    changeWindowOrder(window.id, "left");
  };

  const handleMoveRight = () => {
    changeWindowOrder(window.id, "right");
  };

  const handleToggleChangeStatusPanel = () => {
    handleActivePanelButton(PANEL_BUTTONS.ChangeStatus);
  };

  const handleToggleListsPanel = () => {
    handleActivePanelButton(PANEL_BUTTONS.List);
  };

  return (
    <div
      className={cn("border-gxe-border relative flex h-full shrink-0 flex-col border-r", {
        "transition-all duration-200": !isResizing,
        "w-12": window.state.isMinimized,
        "ring-opacity-30 ring-gxe-primary ml-0.5 ring-2": window.state.isPinned,
      })}
      style={window.state.isMinimized ? undefined : { width }}
    >
      <TaskExplorerToolbar
        currentState={window.state}
        onClose={handleClose}
        onMinimize={handleMinimize}
        isPanelOn={isPanelOn}
        onPin={handlePin}
        onToggleItemPanel={handleToggleItemPanel}
        onMoveLeft={handleMoveLeft}
        onMoveRight={handleMoveRight}
      />

      {!window.state.isMinimized && (
        <div className="flex min-h-0 flex-1 flex-col">
          <Title
            headerClassName="border-b border-gray-200"
            textClassName={cn({ "text-gxe-primary": window.state.isPinned })}
            text={window.state.title ?? "File Explorer"}
            subtitle={window.state.subtitle}
            totalCount={totalLeafCount}
          />
          <TaskPanel
            sortBy={currentSortType}
            sortDirection={sortDirection}
            activeButtons={activePanelButtons}
            onSortChange={handleSortChange}
            onChangeStatusClick={handleToggleChangeStatusPanel}
            onListsClick={handleToggleListsPanel}
          />
          {showChangeStatusPanel && (
            <ChangeStatusPanel
              sortedItems={sortedGroups}
              selectedExplorerItems={window.state.selectedItems ?? new Set()}
              taskIds={window.state.taskIds}
              onClose={() => {
                handleActivePanelButton(PANEL_BUTTONS.ChangeStatus);
              }}
            />
          )}

          {showListsPanel && (
            <ListsPanel
              title={window.state.title}
              sortedItems={sortedGroups}
              windowId={window.id}
              onClose={() => {
                handleActivePanelButton(PANEL_BUTTONS.List);
              }}
            />
          )}

          <div
            ref={containerRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className="min-h-0 flex-1 overflow-y-auto p-2 focus:outline-none"
          >
            <ItemTree
              items={displayItems}
              level={0}
              onItemClick={handleItemClick}
              selectedItems={window.state.selectedItems || new Set()}
              disabledItemIds={disabledItemIds}
              windowId={window.id}
              showGroupId
            />
          </div>
        </div>
      )}

      {window.state.isMinimized && (
        <div className="flex h-full items-center justify-center">
          <div className="rotate-90 text-xs font-medium text-gray-600">{window.state.title}</div>
        </div>
      )}
      {!window.state.isMinimized && (
        <ResizeHandleOnMouseDown handleResizeMouseDown={onResizeStart} />
      )}
    </div>
  );
});

TaskExplorer.displayName = "TaskExplorer";
